import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { map, Observable } from 'rxjs';
// Imported by file rather than through the barrel, so core/auth and core/services do not import
// each other. See the matching note in workspace-context.service.ts.
import { WorkspaceContext } from 'app/core/services/workspace-context.service';
import { AuthGuard } from './auth.guard';

/**
 * Keeps a person inside the part of the application their role belongs to.
 *
 * Every prefix under `/m` names its workspace, so the URL alone says which one a page belongs
 * to. A page that belongs to somebody else's workspace is not translated or partly disabled —
 * the person is sent to the start of their own, because a page reached from the wrong prefix
 * would leave the page itself guessing who is reading it.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceGuard implements CanActivateChild {
  private readonly _router = inject(Router);
  private readonly _workspace = inject(WorkspaceContext);
  private readonly _authGuard = inject(AuthGuard);

  canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // The roles are read off the token, and AuthGuard is what puts them on AuthStateService.
    // Deciding before it has finished would read no roles at all and send everybody to the
    // doctor workspace on a cold load. AdminGuard waits for the same reason.
    return this._authGuard
      .canActivate(next, state)
      .pipe(map((isAuthenticated: boolean): boolean | UrlTree => (isAuthenticated ? this._check(next) : false)));
  }

  private _check(route: ActivatedRouteSnapshot): boolean | UrlTree {
    const declared = this._workspace.declaredOn(route);
    const owned = this._workspace.owned;

    if (declared === null || declared === owned) {
      return true;
    }

    return this._router.parseUrl(this._workspace.homeUrlOf(owned));
  }
}
